import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import i18n from '@shared/i18n';
import { OpenAPIHousien } from '@/services/requests/core/OpenAPI';
import { ApiError } from '@/services/requests/core/ApiError';
import { request as __request } from '@/services/requests/core/request';
import {
  isPresignedPostTarget,
  uploadToPresignedPost,
  type PresignedPostTarget,
} from '@/shared/utils/presigned-upload';

export interface ClientDocument {
  document_id: string;
  filename: string;
  type: string;
  s3_key: string;
  upload_date: string;
  uploaded_by: string;
  file_size?: number;
  download_url?: string;
  /** Link signed to render inline; the download link always saves. */
  view_url?: string;
}

interface UploadLinkResponse {
  document_id: string;
  upload_url: PresignedPostTarget | string;
  s3_key?: string;
}

interface UseClientDocumentsState {
  documents: ClientDocument[];
  loading: boolean;
  error: string | null;
}

type SetState = React.Dispatch<React.SetStateAction<UseClientDocumentsState>>;

/**
 * Normalizes a documents payload into an array.
 *
 * @param data - Raw payload from the documents endpoint
 * @returns Documents array (empty when payload is not a list)
 */
function toDocumentList(data: unknown): ClientDocument[] {
  return Array.isArray(data) ? (data as ClientDocument[]) : [];
}

/**
 * Maps a load error to the next state, treating 404 as empty.
 *
 * @param err - Error thrown while loading documents
 * @returns State to apply after the failed load
 */
function documentsErrorState(err: unknown): UseClientDocumentsState {
  if (err instanceof ApiError && err.status === 404) {
    return { documents: [], loading: false, error: null };
  }
  const fallback = i18n.t('hooks.failedToLoadDocuments', { ns: 'features/clients' });
  const message = err instanceof Error ? err.message : fallback;
  return { documents: [], loading: false, error: message };
}

/**
 * Loads client documents into hook state.
 *
 * @param agencyId - Owning agency identifier
 * @param clientId - Target client identifier
 * @param setState - State setter for the documents hook
 */
async function loadDocuments(
  agencyId: string,
  clientId: string,
  setState: SetState,
): Promise<void> {
  if (!agencyId || !clientId) return;
  setState((prev) => ({ ...prev, loading: true, error: null }));
  try {
    const data = await __request<ClientDocument[]>(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/clients/{client_id}/documents',
      path: { agencyId, client_id: clientId },
    });
    setState({ documents: toDocumentList(data), loading: false, error: null });
  } catch (err) {
    setState(documentsErrorState(err));
  }
}

/**
 * Requests a presigned upload link for a new client document.
 *
 * @param agencyId - Owning agency identifier
 * @param clientId - Target client identifier
 * @param filename - Name of the file to upload
 * @param type - Document type label
 * @returns Upload link response, or null on failure
 */
async function requestUploadLink(
  agencyId: string,
  clientId: string,
  filename: string,
  type: string,
): Promise<UploadLinkResponse | null> {
  try {
    return await __request<UploadLinkResponse>(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/clients/{client_id}/documents/upload-link',
      path: { agencyId, client_id: clientId },
      body: { filename, type },
      mediaType: 'application/json',
    });
  } catch {
    return null;
  }
}

/**
 * Deletes a document and removes it from hook state on success.
 *
 * @param agencyId - Owning agency identifier
 * @param clientId - Target client identifier
 * @param documentId - Identifier of the document to delete
 * @param setState - State setter for the documents hook
 * @returns True when the deletion succeeded
 */
async function removeDocument(
  agencyId: string,
  clientId: string,
  documentId: string,
  setState: SetState,
): Promise<boolean> {
  try {
    await __request(OpenAPIHousien, {
      method: 'DELETE',
      url: '/agency/{agencyId}/clients/{client_id}/documents/{document_id}',
      path: { agencyId, client_id: clientId, document_id: documentId },
    });
    setState((prev) => ({
      ...prev,
      documents: prev.documents.filter((d) => d.document_id !== documentId),
    }));
    return true;
  } catch {
    return false;
  }
}

/**
 * Renames a client document's display filename.
 *
 * @param agencyId - Owning agency identifier
 * @param clientId - Target client identifier
 * @param documentId - Identifier of the document to rename
 * @param filename - New display filename
 * @param setState - State setter for the documents hook
 * @returns True when the rename succeeded
 */
async function renameDocumentRequest(
  agencyId: string,
  clientId: string,
  documentId: string,
  filename: string,
  setState: SetState,
): Promise<boolean> {
  try {
    const updated = await __request<ClientDocument>(OpenAPIHousien, {
      method: 'PATCH',
      url: '/agency/{agencyId}/clients/{client_id}/documents/{document_id}',
      path: { agencyId, client_id: clientId, document_id: documentId },
      body: { filename },
      mediaType: 'application/json',
    });
    setState((prev) => ({
      ...prev,
      documents: prev.documents.map((d) =>
        d.document_id === documentId ? { ...d, filename: updated.filename || filename } : d,
      ),
    }));
    return true;
  } catch {
    return false;
  }
}

/**
 * Manages loading, uploading, renaming and deleting a client's documents.
 *
 * @param agencyId - Owning agency identifier
 * @param clientId - Target client identifier
 * @returns Document list, load flags and mutation callbacks
 */
export function useClientDocuments(agencyId: string, clientId: string) {
  const [state, setState] = useState<UseClientDocumentsState>({
    documents: [],
    loading: true,
    error: null,
  });

  const fetchDocuments = useCallback(
    () => loadDocuments(agencyId, clientId, setState),
    [agencyId, clientId],
  );

  const uploadDocument = useCallback(
    async (file: File) => {
      const link = await requestUploadLink(agencyId, clientId, file.name, file.type);
      if (!link || !isPresignedPostTarget(link.upload_url)) return false;
      await uploadToPresignedPost(link.upload_url, file);
      await fetchDocuments();
      return true;
    },
    [agencyId, clientId, fetchDocuments],
  );

  const deleteDocument = useCallback(
    (documentId: string) => removeDocument(agencyId, clientId, documentId, setState),
    [agencyId, clientId],
  );

  const renameDocument = useCallback(
    (documentId: string, filename: string) =>
      renameDocumentRequest(agencyId, clientId, documentId, filename, setState),
    [agencyId, clientId],
  );

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  return { ...state, refetch: fetchDocuments, uploadDocument, deleteDocument, renameDocument };
}
